import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import 'jspdf-autotable';
import { AnalyticsOverviewResponse } from '../types/adminAnalytics.ts';
import { formatDeltaPercent, trendLabel } from './adminAnalyticsHelpers.ts';

const BRAND_COLOR = [35, 75, 162] as const;
const CHART_CAPTURE_SELECTOR = '[data-analytics-export-chart]';
const DEFAULT_EXPORT_ROOT_ID = 'admin-analytics-export-root';

type CapturedImage = {
  dataUrl: string;
  width: number;
  height: number;
};

async function captureChartImages(exportRootId: string): Promise<CapturedImage[]> {
  const root = document.getElementById(exportRootId);
  if (!root) return [];

  const nodes = Array.from(root.querySelectorAll(CHART_CAPTURE_SELECTOR));
  const images: CapturedImage[] = [];

  for (const node of nodes) {
    const canvas = await html2canvas(node as HTMLElement, {
      scale: 2,
      backgroundColor: '#ffffff',
      logging: false,
      useCORS: true,
    });
    images.push({
      dataUrl: canvas.toDataURL('image/png'),
      width: canvas.width,
      height: canvas.height,
    });
  }

  return images;
}

function addChartImagesToPdf(
  doc: jsPDF,
  images: CapturedImage[],
  startY: number,
  margin: number
): number {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - margin * 2;
  let y = startY;

  images.forEach((image) => {
    const imgHeight = (image.height * contentWidth) / image.width;
    if (y + imgHeight > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
    doc.addImage(image.dataUrl, 'PNG', margin, y, contentWidth, imgHeight);
    y += imgHeight + 10;
  });

  return y;
}

export async function saveAdminAnalyticsPdf(
  overview: AnalyticsOverviewResponse,
  exportRootId: string = DEFAULT_EXPORT_ROOT_ID
): Promise<void> {
  await new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)));
  await new Promise((resolve) => setTimeout(resolve, 150));

  const chartImages = await captureChartImages(exportRootId);
  const { meta, rows } = overview.report;
  const doc = new jsPDF({ orientation: 'landscape' });
  const margin = 14;
  const pageHeight = doc.internal.pageSize.getHeight();

  doc.setFontSize(16);
  doc.setTextColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
  doc.text(meta.title, margin, 16);

  doc.setFontSize(10);
  doc.setTextColor(80, 80, 80);
  doc.text(`Período: ${meta.periodLabel}`, margin, 24);
  doc.text(`Comparación: ${meta.comparePeriodLabel}`, margin, 30);
  doc.text(
    `Generado: ${new Date(meta.generatedAt).toLocaleString('es-AR')}`,
    margin,
    36
  );

  let cursorY = 42;

  if (chartImages.length > 0) {
    doc.setFontSize(11);
    doc.setTextColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
    doc.text('Gráficos comparativos', margin, cursorY + 4);
    cursorY += 10;
    cursorY = addChartImagesToPdf(doc, chartImages, cursorY, margin);
  }

  if (cursorY + 40 > pageHeight - margin) {
    doc.addPage();
    cursorY = margin;
  }

  doc.setFontSize(11);
  doc.setTextColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
  doc.text('Detalle de métricas', margin, cursorY + 4);

  const tableBody = rows.map((row) => [
    row.sectionLabel,
    row.metricLabel,
    String(row.current),
    String(row.previous),
    String(row.delta),
    formatDeltaPercent(row.deltaPercent),
    trendLabel(row.trend),
  ]);

  (doc as any).autoTable({
    startY: cursorY + 8,
    head: [['Sección', 'Métrica', 'Actual', 'Anterior', 'Delta', '%', 'Tendencia']],
    body: tableBody,
    styles: { fontSize: 8 },
    headStyles: { fillColor: BRAND_COLOR },
    margin: { left: margin, right: margin },
    pageBreak: 'auto',
  });

  const dateStamp = new Date().toISOString().slice(0, 10);
  doc.save(`edify-analytics-${dateStamp}.pdf`);
}

export const ADMIN_ANALYTICS_EXPORT_ROOT_ID = DEFAULT_EXPORT_ROOT_ID;
